import React from "react";
import { Link } from "react-router-dom";

const styles = {
  container: {
    maxWidth: "720px",
    margin: "0 auto",
    padding: "40px 24px",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    lineHeight: "1.7",
    color: "#333",
    backgroundColor: "#fafafa",
    minHeight: "100vh",
  } as React.CSSProperties,
  header: {
    textAlign: "center" as const,
    marginBottom: "48px",
    paddingBottom: "32px",
    borderBottom: "2px solid #e0e0e0",
  } as React.CSSProperties,
  title: {
    fontSize: "2rem",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: "0 0 8px 0",
  } as React.CSSProperties,
  subtitle: {
    fontSize: "1rem",
    color: "#666",
    margin: "0 0 12px 0",
  } as React.CSSProperties,
  lastUpdated: {
    fontSize: "0.85rem",
    color: "#888",
    margin: 0,
  } as React.CSSProperties,
  section: {
    marginBottom: "32px",
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: "1.4rem",
    fontWeight: 600,
    color: "#2c5282",
    marginBottom: "16px",
    paddingBottom: "8px",
    borderBottom: "1px solid #cbd5e0",
  } as React.CSSProperties,
  subsectionTitle: {
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#2d3748",
    marginBottom: "8px",
    marginTop: "20px",
  } as React.CSSProperties,
  card: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "20px",
    marginBottom: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  } as React.CSSProperties,
  overviewBox: {
    backgroundColor: "#f0fff4",
    border: "1px solid #9ae6b4",
    borderRadius: "8px",
    padding: "20px",
    marginBottom: "32px",
  } as React.CSSProperties,
  notDoBox: {
    backgroundColor: "#fff5f5",
    border: "1px solid #feb2b2",
    borderRadius: "8px",
    padding: "20px",
  } as React.CSSProperties,
  text: {
    color: "#4a5568",
    margin: "0 0 12px 0",
  } as React.CSSProperties,
  list: {
    margin: "12px 0",
    paddingLeft: "24px",
    color: "#4a5568",
  } as React.CSSProperties,
  contactBox: {
    backgroundColor: "#ebf8ff",
    border: "1px solid #90cdf4",
    borderRadius: "8px",
    padding: "20px",
    textAlign: "center" as const,
  } as React.CSSProperties,
  contactEmail: {
    color: "#2b6cb0",
    fontWeight: 600,
    textDecoration: "none",
  } as React.CSSProperties,
  footer: {
    marginTop: "48px",
    paddingTop: "24px",
    borderTop: "1px solid #e0e0e0",
    textAlign: "center" as const,
    color: "#666",
    fontSize: "0.9rem",
  } as React.CSSProperties,
  link: {
    color: "#2b6cb0",
    textDecoration: "none",
  } as React.CSSProperties,
};

const MomentalPrivacy: React.FC = () => {
  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Momental: Maze Puzzles</h1>
        <p style={styles.subtitle}>Privacy Policy</p>
        <p style={styles.lastUpdated}>Last updated: January 29, 2026</p>
      </header>

      <p style={styles.text}>
        This privacy notice explains how Momental: Maze Puzzles handles data.
      </p>

      <div style={styles.overviewBox}>
        <h2 style={{ ...styles.sectionTitle, borderBottom: "none", marginBottom: "8px", paddingBottom: 0, color: "#276749" }}>
          Overview
        </h2>
        <p style={{ margin: 0, color: "#2f855a" }}>
          Momental is designed to respect your privacy. We collect no personal
          information, require no accounts, and display no ads.
        </p>
      </div>

      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Data We Collect</h2>

        <div style={styles.card}>
          <h3 style={{ ...styles.subsectionTitle, marginTop: 0 }}>
            Analytics
          </h3>
          <p style={styles.text}>
            Momental uses Firebase Analytics to understand general gameplay behavior.
          </p>
          <p style={styles.text}>
            An anonymous identifier is used to understand gameplay patterns across
            sessions. This identifier contains no personal information and cannot be
            used to identify you.
          </p>
          <p style={styles.text}>
            Analytics data includes: session timing, level completion and abandonment
            events, and community level interactions (plays, uploads, ratings).
          </p>
          <ul style={{ ...styles.list, marginBottom: 0 }}>
            <li>No names, email addresses, or personal identifiers are collected</li>
            <li>Used only to improve app stability and gameplay</li>
          </ul>
        </div>

        <div style={styles.card}>
          <h3 style={{ ...styles.subsectionTitle, marginTop: 0 }}>Game progress</h3>
          <p style={styles.text}>
            Game progress is synced across your devices using Apple iCloud (CloudKit),
            if enabled.
          </p>
          <ul style={{ ...styles.list, marginBottom: 0 }}>
            <li>Progress data is stored and managed by Apple</li>
            <li>We do not access or store this data on our own servers</li>
          </ul>
        </div>

        <div style={styles.card}>
          <h3 style={{ ...styles.subsectionTitle, marginTop: 0 }}>Game Center</h3>
          <p style={styles.text}>
            If you choose to upload community levels, Apple Game Center is used to
            uniquely identify contributors.
          </p>
          <ul style={{ ...styles.list, marginBottom: 0 }}>
            <li>Participation is optional</li>
            <li>You control your Game Center display name</li>
            <li>No personal information is required to play the game</li>
          </ul>
        </div>
      </section>

      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>What We Do Not Do</h2>
        <div style={styles.notDoBox}>
          <ul style={{ ...styles.list, margin: 0, color: "#c53030" }}>
            <li>We do not collect names, email addresses, or contact information</li>
            <li>We do not track users across apps or websites</li>
            <li>We do not display ads</li>
            <li>We do not sell or share data with third parties</li>
          </ul>
          <p style={{ margin: "16px 0 0 0", color: "#742a2a" }}>
            Momental does not use App Tracking Transparency (ATT), because no
            cross-app tracking occurs.
          </p>
        </div>
      </section>

      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Internet Usage</h2>
        <div style={styles.card}>
          <p style={styles.text}>An internet connection is required only for:</p>
          <ul style={styles.list}>
            <li>Browsing community levels</li>
            <li>Uploading community levels</li>
          </ul>
          <p style={{ margin: 0, color: "#4a5568" }}>
            All built-in levels and downloaded community levels can be played offline.
          </p>
        </div>
      </section>

      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Children's Privacy</h2>
        <div style={styles.card}>
          <p style={{ margin: 0, color: "#4a5568" }}>
            Momental does not knowingly collect personal information from children.
            The game contains no advertising and no user messaging features.
          </p>
        </div>
      </section>

      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Changes to This Policy</h2>
        <p style={styles.text}>
          If this privacy notice changes, the updated version will be posted on this
          page.
        </p>
      </section>

      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Contact</h2>
        <div style={styles.contactBox}>
          <p style={{ margin: "0 0 8px 0", color: "#4a5568" }}>
            If you have questions about privacy or data usage, contact:
          </p>
          <a href="mailto:MomentalFeedback@gmail.com" style={styles.contactEmail}>
            MomentalFeedback@gmail.com
          </a>
        </div>
      </section>

      <footer style={styles.footer}>
        <p>
          <a href="https://momental.craftword.game/Support" style={styles.link}>
            Support & Help Center
          </a>
          {" | "}
          <a href="https://momental.craftword.game" style={styles.link}>
            Home
          </a>
        </p>
        <p style={{ marginTop: "8px" }}>© 2026 Momental</p>
      </footer>
    </div>
  );
};

export default MomentalPrivacy;
